import { Link, useParams, Navigate } from "react-router-dom";
import { User, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHead, Section, Reveal, CtaBand } from "@/components/Shared";
import { UI, PATHS } from "@/i18n/ui";
import { PSYCHOLOGISTS, PROFILE_FIELDS } from "@/data/psychologists";
import { SITE } from "@/data/site";

export function PsychologistsPage({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const meta = {
    id: { title: "Tim Psikolog Bali Empower di Denpasar, Bali", desc: "Kenali tim tenaga profesional psikologi Bali Empower di Denpasar, Bali. Profil lengkap termasuk kredensial, pendidikan, dan area praktik akan segera dilengkapi." },
    en: { title: "Bali Empower Psychologists in Denpasar, Bali", desc: "Meet Bali Empower's psychology professionals in Denpasar, Bali. Full profiles including credentials, education and areas of practice will be completed soon." },
  }[lang];
  return (
    <div data-testid="psychologists-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} />
      <PageHead eyebrow={t.nav.psychologists} title={t.sections.team} sub={t.sections.teamSub} />
      <Section className="pt-0">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PSYCHOLOGISTS.map((psy, i) => (
            <Reveal key={psy.slug} delay={i * 0.05}>
              <Link to={p.psychologist(psy.slug)} data-testid={`psychologists-index-card-${i}`}
                className="be-card block rounded-2xl border border-[#E5E1D8] bg-white overflow-hidden">
                <div className="h-52 bg-[#F2EFE9] flex items-center justify-center overflow-hidden">
                  {psy.photo ? <img src={psy.photo} alt={psy.name[lang]} className="w-full h-full object-cover" /> : <User size={48} className="text-[#C4A484]" strokeWidth={1.2} />}
                </div>
                <div className="p-6">
                  <h2 className="font-display text-lg text-[#1F2937]">{psy.name[lang]}</h2>
                  <p className="mt-1 text-sm text-[#4B5563]">{psy.title[lang]}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-[#4A6B53]">
                    {t.misc.viewProfile} <ArrowRight size={13} />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}

const ProfileFields = ({ psy, lang }) => (
  <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-6">
    {PROFILE_FIELDS.map((f) => {
      const v = psy.details?.[f.key];
      return (
        <div key={f.key} className="border-b border-[#E5E1D8] pb-4">
          <h3 className="text-xs uppercase tracking-[0.15em] text-[#4A6B53] font-sans font-semibold">{f.label[lang]}</h3>
          <p className={`mt-1.5 text-sm text-[#4B5563] ${v ? "" : "italic"}`}>{v ? v[lang] : psy.placeholder[lang]}</p>
        </div>
      );
    })}
  </div>
);

const ProfileAside = ({ psy, lang, t, p }) => (
  <aside>
    <div className="rounded-2xl border border-[#E5E1D8] bg-white p-8 sticky top-24">
      <div className="h-40 rounded-xl bg-[#F2EFE9] flex items-center justify-center mb-6 overflow-hidden">
        {psy.photo ? <img src={psy.photo} alt={psy.name[lang]} className="w-full h-full object-cover" /> : <User size={40} className="text-[#C4A484]" strokeWidth={1.2} />}
      </div>
      <Link to={p.booking} data-testid="psychologist-book-btn"
        className="flex items-center justify-center gap-2 rounded-full bg-[#4A6B53] text-white font-medium px-6 py-3.5 hover:bg-[#3A5542] transition-colors">
        {t.misc.bookWith} <ArrowRight size={15} />
      </Link>
    </div>
  </aside>
);

export function PsychologistDetail({ lang }) {
  const t = UI[lang];
  const p = PATHS[lang];
  const { slug } = useParams();
  const psy = PSYCHOLOGISTS.find((x) => x.slug === slug);
  if (!psy) return <Navigate to={p.psychologists} replace />;
  const meta = {
    id: { title: `${psy.name.id} | Psikolog Bali Empower Denpasar`, desc: `Profil ${psy.name.id} di Bali Empower, klinik psikologi di Renon, Denpasar Selatan, Bali. Detail kredensial dan area praktik akan segera dilengkapi.` },
    en: { title: `${psy.name.en} | Bali Empower Psychologist, Denpasar`, desc: `Profile of ${psy.name.en} at Bali Empower, a psychology clinic in Renon, Denpasar Selatan, Bali. Credentials and practice details will be completed soon.` },
  }[lang];
  const jsonLd = [{
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE.url}/psychologists/#${psy.slug}`,
    name: psy.name[lang],
    jobTitle: "Psychologist",
    worksFor: { "@id": "https://baliempower.com/#organization" },
  }];
  return (
    <div data-testid="psychologist-detail-page">
      <Seo title={meta.title} description={meta.desc} lang={lang} jsonLd={jsonLd} />
      <PageHead eyebrow={t.nav.psychologists} title={psy.name[lang]} sub={psy.title[lang]} />
      <Section className="pt-0">
        <div className="grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <p className="text-[#4B5563] leading-relaxed">{psy.bio[lang]}</p>
            <ProfileFields psy={psy} lang={lang} />
          </div>
          <ProfileAside psy={psy} lang={lang} t={t} p={p} />
        </div>
      </Section>
      <CtaBand lang={lang} />
    </div>
  );
}
